import {
  denied,
  failed,
  ok,
  unsupported,
  type ClipboardCapability,
  type HostLike,
  type Outcome,
  type PlatformCapabilities,
} from './types'

/**
 * Web 那一档的实现。
 *
 * # 降级必须诚实
 *
 * 这一层的规矩只有一条：**做不到就说做不到**，不用别的东西顶替。
 *
 * 具体到还没进这个包的那几个能力，这条规矩长这样：
 * 钥匙串的降级**不是** localStorage——那不是降级，是把本该由操作系统
 * 保管的东西放进同源脚本随手可读的地方，比「没有这个功能」更糟。
 * 一个假装成功的兜底会让整条链路看起来在跑（CLAUDE.md：
 * 「一个假装成功的接口比没有这个接口更能骗人」）。
 *
 * 所以 Web 实现只有两种合法形态：**真的做到**，或者 **`ok: false`**。
 * 没有第三种。
 */

interface ClipboardLike {
  writeText?: (text: string) => Promise<void>
}

interface NavigatorLike {
  clipboard?: ClipboardLike
}

/** 从宿主里取 `navigator`，取不到回 null（Node、SSR、被 mock 掉的宿主）。 */
function navigatorOf(host: HostLike): NavigatorLike | null {
  const nav = host['navigator']
  return typeof nav === 'object' && nav !== null ? (nav as NavigatorLike) : null
}

/**
 * 把一次 rejection 归到 `denied` 还是 `failed`。
 *
 * `NotAllowedError` 是规范给权限拒绝定的名字（Safari 在「不是用户手势触发」
 * 时也用它）。**只认这一个名字，认不出的一律 `failed`**——
 * 猜错的方向要选对：把「权限问题」说成「再试一次」，用户至多白点一次；
 * 把「这一次没成」说成「你被拒了」，用户会去改一个根本没问题的设置。
 */
function classify(cause: unknown): Outcome {
  const name = typeof cause === 'object' && cause !== null ? (cause as { name?: unknown }).name : undefined
  return name === 'NotAllowedError' ? denied : failed
}

export function webClipboard(host: HostLike): ClipboardCapability {
  return {
    async copyText(text: string): Promise<Outcome> {
      let clipboard: ClipboardLike | undefined
      try {
        clipboard = navigatorOf(host)?.clipboard
      } catch {
        // 读这两个属性本身就抛——隐私模式、被扩展改过的 navigator 都干过。
        // 「够不着这条路」与「这条路不存在」对用户是同一件事：手动选中文本。
        return unsupported
      }
      const writeText = clipboard?.writeText
      // 非安全上下文（http:// 上的局域网访问）、老浏览器、jsdom 都落在这儿。
      // 这不是「这一次没成」，是这条路在这个宿主里不存在——两者要求用户做的
      // 下一个动作不同，所以不能合成一句。
      if (typeof writeText !== 'function') return unsupported
      try {
        await writeText.call(clipboard, text)
        return ok
      } catch (cause) {
        return classify(cause)
      }
    },
  }
}

export function webCapabilities(host: HostLike): PlatformCapabilities {
  return { surface: 'web', clipboard: webClipboard(host) }
}
